import { useId, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Form, Label, Modal, TextArea } from '@shared/components';
import {
    REVERSAL_NOTE_MAX_LENGTH,
    reverseInvoiceSchema,
    type ReverseInvoiceFormInput,
    type ReverseInvoiceFormValues,
} from '../../schemas/reverseInvoiceSchema';
import { formatInvoiceMoney } from '../../utils/formatInvoiceMoney';
import type { AgreementInvoice } from '../../types/agreements.types';

interface Props {
    isOpen: boolean;
    invoice: Pick<AgreementInvoice, 'invoiceNumber' | 'totalGross'>;
    pending: boolean;
    serverError: string | null;
    onSubmit: (note: string) => void;
    onClose: () => void;
}

const CONSEQUENCES = [
    'Items return to Still to invoice',
    'A minus transaction is raised on the ledger',
    'Any OnClaim fees on this invoice are reversed',
];

export function ReverseInvoiceModal({ isOpen, invoice, pending, serverError, onSubmit, onClose }: Props) {
    const headingId = useId();
    const noteFieldRef = useRef<HTMLDivElement>(null);

    const handleClose = () => {
        if (pending) return;
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!pending}
            ariaLabelledBy={headingId}
            className="max-w-lg p-6"
            initialFocusRef={noteFieldRef}
        >
            <h2 id={headingId} className="mb-1 text-lg font-semibold">
                Reverse invoice {invoice.invoiceNumber}
            </h2>
            <p className="mb-4 text-sm text-mp-muted">
                Gross value <span className="font-semibold tabular-nums">{formatInvoiceMoney(invoice.totalGross)}</span>
            </p>
            <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
                {CONSEQUENCES.map((line) => (
                    <li key={line}>{line}</li>
                ))}
            </ul>
            <div className="space-y-4">
                {serverError && <Alert variant="error">{serverError}</Alert>}
                <ReverseInvoiceForm
                    pending={pending}
                    noteFieldRef={noteFieldRef}
                    onSubmit={onSubmit}
                    onCancel={handleClose}
                />
            </div>
        </Modal>
    );
}

interface FormProps {
    pending: boolean;
    noteFieldRef: React.RefObject<HTMLDivElement | null>;
    onSubmit: (note: string) => void;
    onCancel: () => void;
}

function ReverseInvoiceForm({ pending, noteFieldRef, onSubmit, onCancel }: FormProps) {
    const noteId = useId();
    const {
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm<ReverseInvoiceFormInput, unknown, ReverseInvoiceFormValues>({
        resolver: zodResolver(reverseInvoiceSchema),
        mode: 'onBlur',
        defaultValues: { note: '' },
        shouldFocusError: false,
    });
    const note = watch('note');
    const noteError = errors.note?.message;

    return (
        <Form
            onSubmit={handleSubmit(
                (values) => onSubmit(values.note),
                () => noteFieldRef.current?.querySelector('textarea')?.focus()
            )}
            noValidate
            className="space-y-4"
        >
            <div ref={noteFieldRef}>
                <Label htmlFor={noteId} required>
                    Reason for reversal
                </Label>
                <TextArea
                    id={noteId}
                    rows={5}
                    placeholder="Why is this invoice being reversed?"
                    value={note}
                    onChange={(value) => setValue('note', value, { shouldValidate: !!noteError })}
                    error={!!noteError}
                    disabled={pending}
                    hint={noteError}
                    hintId={`${noteId}-error`}
                    required
                />
                <p className="mt-1 text-right text-xs text-mp-muted" aria-live="polite">
                    {note.length} / {REVERSAL_NOTE_MAX_LENGTH}
                </p>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                    type="button"
                    variant="neutral"
                    appearance="ghost"
                    disabled={pending}
                    onClick={onCancel}
                    className="w-full sm:w-auto"
                >
                    Cancel
                </Button>
                <Button type="submit" variant="danger" appearance="solid" disabled={pending} className="w-full sm:w-auto">
                    {pending ? 'Reversing...' : 'Reverse invoice'}
                </Button>
            </div>
        </Form>
    );
}
